<template>
  <div class="container-fluid px-0">
    <div class="row mb-4 g-2">
      <div class="col-6 col-md-2 col-lg-2">
        <input
          type="date"
          class="form-control form-control-sm"
          v-model="localFilters.date"
          @change="applyFilters"
        >
      </div>

      <div class="col-6 col-md-2 col-lg-2">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.status"
          @change="applyFilters"
        >
          <option value="">{{ $t('attendance.allStatuses') }}</option>
          <option value="present">{{ $t('attendance.present') }}</option>
          <option value="absent">{{ $t('attendance.absent') }}</option>
          <option value="late">{{ $t('attendance.late') }}</option>
          <option value="excused">{{ $t('attendance.excused') }}</option>
        </select>
      </div>

      <div class="col-12 col-md-2 col-lg-2" v-if="!isTeacher || teacherClasses.length > 0">
        <select
          class="form-select form-select-sm"
          v-model="localFilters.class_id"
          @change="onClassChange"
        >
          <option value="">{{ $t('attendance.allClasses') }}</option>
          <option
            v-for="classItem in filteredClasses"
            :key="classItem.id"
            :value="classItem.id"
          >
            {{ classItem.name }}
          </option>
        </select>
      </div>

      <div class="col-md-1 d-none d-md-block">
        <button
          class="btn btn-outline-secondary btn-sm w-100"
          @click="resetFilters"
          :title="$t('attendance.reset')"
        >
          <i class="fas fa-filter-circle-xmark"></i>
        </button>
      </div>

      <div class="col-12 col-md-3 col-lg-3 position-relative">
        <input
          type="text"
          class="form-control form-control-sm ps-5"
          :placeholder="$t('attendance.searchByStudent')"
          v-model="localFilters.search"
          @input="handleSearchInput"
          style="padding-right: 12px;"
        />
        <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
          <i class="fas fa-search"></i>
        </span>
      </div>

      <div class="col-12 d-md-none">
        <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
          <i class="fas fa-filter-circle-xmark"></i> {{ $t('attendance.resetMobile') }}
        </button>
      </div>

      <div class="col-12 col-md-2 col-lg-2">
        <router-link
          to="/attendances/record"
          class="btn btn-secondary btn-sm w-100"
          v-can="'student_attendance.create'"
        >
          <i class="fas fa-plus"></i>
          {{ $t('attendance.addAttendance') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { debounce } from 'lodash';
import { useStore } from 'vuex';
import Cookies from 'js-cookie';

const store = useStore();

const props = defineProps({
  classes: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: () => ({})
  },
  teacherClasses: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['filter']);

const currentUser = computed(() => {
  return store.state.user || {
    role: {
      slug: Cookies.get('user_type') || 'teacher'
    }
  };
});

const isTeacher = computed(() => {
  return currentUser.value?.role?.slug === 'teacher';
});

const filteredClasses = computed(() => {
  return isTeacher.value ? props.teacherClasses : props.classes;
});

const localFilters = ref({
  date: props.filters?.date || '',
  status: props.filters?.status || '',
  class_id: props.filters?.class_id || '',
  search: props.filters?.search || ''
});

const applyFilters = () => {
  emit('filter', { ...localFilters.value });
};

const handleSearchInput = debounce(() => {
  applyFilters();
}, 300);

const onClassChange = () => {
  applyFilters();
};

const resetFilters = () => {
  localFilters.value = {
    date: '',
    status: '',
    class_id: '',
    search: ''
  };

  applyFilters();
};

watch(
  () => props.filters,
  (newVal) => {
    localFilters.value = {
      date: newVal?.date || '',
      status: newVal?.status || '',
      class_id: newVal?.class_id || '',
      search: newVal?.search || ''
    };
  },
  { deep: true, immediate: true }
);
</script>